import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import EtfInProgressOrders from './EtfInProgressOrders';
import EtfCalculatedOrders from './EtfCalculatedOrders';
import EtfProcessedInstructions from './EtfProcessedInstructions';
import EtfCompletedOrders from './EtfCompletedOrders';
import OrdersPageLayout, { OrderTab, OrderActionButton } from '../orders/OrdersPageLayout';

type Tab = 'inprogress' | 'processed' | 'calculated' | 'completed';

interface LocationState {
    message?: string;
    activeTab?: Tab;
}

const TABS: OrderTab[] = [
    { key: 'inprogress', label: 'In-Progress' },
    { key: 'processed', label: 'Processed Instructions' },
    { key: 'calculated', label: 'Calculated Orders' },
    { key: 'completed', label: 'Completed' },
];

const EtfOrdersList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { message, activeTab: initialActiveTab } = (location.state as LocationState) || {};

    const [activeTab, setActiveTab] = useState<Tab>(initialActiveTab ?? 'inprogress');
    const [successMessage, setSuccessMessage] = useState<string | null>(message ?? null);

    useEffect(() => {
        if (!successMessage) return;
        const timer = setTimeout(() => setSuccessMessage(null), 5000);
        return () => clearTimeout(timer);
    }, [successMessage]);

    // Staying put while instruments are still unprocessed is the point: on a same-day
    // scheme one sell order per liquid instrument is raised, and they are processed one
    // at a time so each sees what the previous one already covered.
    const handleProcessSuccess = (remaining: number) => {
        if (remaining > 0) return;
        setSuccessMessage('ETF orders processed successfully');
        setActiveTab('calculated');
    };

    const handleOrdersSubmitted = () => {
        setSuccessMessage('ETF orders submitted successfully');
        setActiveTab('completed');
    };

    const handleDeleteSuccess = () => {
        setSuccessMessage('ETF instructions deleted successfully');
        setActiveTab('processed');
    };

    const actionButtons: OrderActionButton[] = [
        { label: 'New ETF Buy Order', onClick: () => navigate('/etf-orders/buy/new'), variant: 'buy' },
        { label: 'New ETF Sell Order', onClick: () => navigate('/etf-orders/sell/new'), variant: 'sell' },
    ];

    return (
        <OrdersPageLayout
            title="ETF Orders"
            subtitle="ETF instructions through to submitted orders, one stage per tab."
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as Tab)}
            actionButtons={actionButtons}
            successMessage={successMessage}
        >
            {activeTab === 'inprogress' && <EtfInProgressOrders onProcessSuccess={handleProcessSuccess} />}
            {activeTab === 'processed' && <EtfProcessedInstructions onDeleteSuccess={handleDeleteSuccess} />}
            {activeTab === 'calculated' && <EtfCalculatedOrders onOrdersSubmitted={handleOrdersSubmitted} />}
            {activeTab === 'completed' && <EtfCompletedOrders />}
        </OrdersPageLayout>
    );
};

export default EtfOrdersList;